import type { ReactNode } from 'react';
import {
  Badge,
  Box,
  Flex,
  Group,
  NumberFormatter,
  Paper,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { safeCastNumber } from 'utils/numberUtils';
import { useProviderDashboardPalette } from './useProviderDashboardPalette';

interface PayPeriodSummaryCardProps {
  title: string;
  badge: string;
  badgeColor: string;
  titleContext?: string;
  topRightBadge?: ReactNode;
  isLoading?: boolean;
  rows: Array<{
    label: string;
    value: string | number;
    onClick?: () => void;
    color?: string;
    tooltip?: string;
  }>;
}

export const PayPeriodSummaryCard = ({
  title,
  badge,
  badgeColor,
  titleContext,
  topRightBadge,
  isLoading = false,
  rows,
}: PayPeriodSummaryCardProps) => {
  const palette = useProviderDashboardPalette();

  return (
    <Paper
      p="lg"
      radius="lg"
      style={{
        background: palette.cardBackground,
        border: `1px solid ${palette.cardBorder}`,
        minHeight: 180,
      }}
    >
      <Stack gap={10} mb="md">
        <Flex justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Stack gap={6}>
            <Group gap={10} align="center">
              <Text fw={700} size="lg" c={palette.primaryText}>
                {title}
              </Text>
              {titleContext ? (
                <Badge
                  size="sm"
                  radius="xl"
                  variant="light"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(75, 150, 241, 0.2), rgba(89, 199, 154, 0.18))',
                    color: '#DCEBFF',
                    border: '1px solid rgba(143, 182, 255, 0.28)',
                    textTransform: 'none',
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    boxShadow: '0 10px 24px rgba(9, 14, 24, 0.18)',
                  }}
                >
                  {titleContext}
                </Badge>
              ) : null}
            </Group>
          </Stack>
          {topRightBadge && (
            <Box
              ml="auto"
              style={{ flexShrink: 0 }}
              data-testid="summary-card-top-right-badge"
            >
              {topRightBadge}
            </Box>
          )}
        </Flex>
        <Badge
          size="md"
          radius="xl"
          variant="filled"
          style={{
            alignSelf: 'flex-start',
            background: badgeColor,
            color: palette.summaryBadgeText,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {badge}
        </Badge>
      </Stack>

      {isLoading ? (
        <Stack gap={10} data-testid="provider-summary-card-skeleton">
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
          <Skeleton height={18} />
        </Stack>
      ) : (
        <Stack gap={10}>
          {rows.map((row) => (
            <Flex
              key={row.label}
              justify="space-between"
              align="center"
              onClick={row.onClick}
              role={row.onClick ? 'button' : undefined}
              tabIndex={row.onClick ? 0 : undefined}
              onKeyDown={
                row.onClick
                  ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        row.onClick?.();
                      }
                    }
                  : undefined
              }
              style={{ cursor: row.onClick ? 'pointer' : 'default' }}
            >
              <Tooltip
                label={row.tooltip}
                disabled={!row.tooltip}
                withArrow
                multiline
                w={220}
                position="top-start"
              >
                <Group gap={6}>
                  <Text
                    size="sm"
                    c={row.color || palette.mutedText}
                    style={{ cursor: row.tooltip ? 'help' : undefined }}
                  >
                    {row.label}
                  </Text>
                  {row.tooltip ? (
                    <Box
                      component="span"
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 999,
                        border: `1px solid ${palette.hintBorder}`,
                        color: palette.hintText,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: 1,
                        cursor: 'help',
                        flexShrink: 0,
                      }}
                    >
                      ?
                    </Box>
                  ) : null}
                </Group>
              </Tooltip>
              {typeof row.value === 'number' ? (
                <NumberFormatter
                  prefix="$"
                  value={safeCastNumber(row.value as number)}
                  thousandSeparator
                  decimalScale={2}
                  fixedDecimalScale
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: palette.primaryText,
                  }}
                />
              ) : (
                <Text size="sm" fw={700} c={palette.primaryText}>
                  {row.value}
                </Text>
              )}
            </Flex>
          ))}
        </Stack>
      )}
    </Paper>
  );
};
